import { Box, Button, Stack } from '@mui/material'
import * as Styled from './PollResultsModals.styles'
import BaseModal from '../BaseModal/BaseModal'
import { useCallback, useEffect, useRef, useState } from 'react'
import PeopleIcon from '@mui/icons-material/People'
import { Poll } from '../../assets'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { getPollResults, updatePollStatus } from '../../services/appsync'
import {
  POLL_STATUS,
  PollResult,
  transformPollResultsResponse,
} from '../CreatedPollModal/PollDataModel'

type PollResultsModalProps = {
  open: boolean
  onClose: () => void
  variant?: 'live-event' | 'past-event' | 'upcoming-event'
  onPushNextPoll?: () => void
  passedPoll?: { pollId: string; eventId: string }
  organizerOrAdmin?: boolean | null
  pollAlreadyClosed?: boolean
}

const PollResultsModal = ({
  open,
  onClose,
  variant,
  onPushNextPoll,
  passedPoll,
  organizerOrAdmin = true,
  pollAlreadyClosed = false,
}: PollResultsModalProps) => {
  const [showPollResults, setShowPollResults] = useState(false)
  const [allPolls, setAllPolls] = useState<PollResult[]>([])
  const [animate, setAnimate] = useState(false)
  const [loading, setLoading] = useState(false)
  const isPastEvent = variant === 'past-event'
  const hasLoadedData = useRef(false)

  const [currentPollIndex, setCurrentPollIndex] = useState(0)
  const pollData = allPolls[currentPollIndex]

  useEffect(() => {
    if (!open) {
      hasLoadedData.current = false
      setAllPolls([])
      setCurrentPollIndex(0)
      setShowPollResults(false)
      setAnimate(false)
      return
    }

    const timer = setTimeout(() => {
      setAnimate(true)
      if (isPastEvent) {
        setShowPollResults(true)
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [open, isPastEvent])

  const handleClosePoll = async () => {
    if (passedPoll && organizerOrAdmin) {
      await updatePollStatus(
        passedPoll.eventId,
        passedPoll.pollId,
        POLL_STATUS.COMPLETED
      )
      await collectPollResults()
    }
    setShowPollResults(true)
  }

  const handleNextPoll = () => {
    if (currentPollIndex < allPolls.length - 1) {
      setAnimate(false)
      setCurrentPollIndex((prev) => prev + 1)
    }
  }

  const handlePreviousPoll = () => {
    if (currentPollIndex > 0) {
      setAnimate(false)
      setCurrentPollIndex((prev) => prev - 1)
    }
  }

  const collectPollResults = useCallback(async () => {
    if (organizerOrAdmin && passedPoll) {
      setLoading(true)
      const pollResponses = await getPollResults(passedPoll.eventId)

      const newPollTransform = transformPollResultsResponse([pollResponses])

      setAllPolls(newPollTransform)
      setLoading(false)
    } else if (passedPoll) {
      setLoading(true)
      const pollResponses = await getPollResults(
        passedPoll.eventId,
        passedPoll.pollId
      )

      const newPollTransform = transformPollResultsResponse([pollResponses])

      setAllPolls(newPollTransform)
      setLoading(false)
    }
  }, [organizerOrAdmin, passedPoll])

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100)
    return () => clearTimeout(timer)
  }, [currentPollIndex])

  useEffect(() => {
    if (open && passedPoll && !hasLoadedData.current) {
      hasLoadedData.current = true
      if (!organizerOrAdmin) {
        setShowPollResults(true)
      }
      collectPollResults()
    }
  }, [open, organizerOrAdmin, passedPoll, collectPollResults])

  // Set current poll index to match the selected poll
  useEffect(() => {
    if (passedPoll && allPolls.length > 0) {
      const selectedIndex = allPolls.findIndex(
        (poll) => poll.pollId === passedPoll.pollId
      )
      if (selectedIndex !== -1) {
        setCurrentPollIndex(selectedIndex)
      }
    }
  }, [allPolls, passedPoll])

  return (
    <Box>
      <BaseModal
        overflow="auto"
        desktopWidth={
          variant === 'past-event'
            ? '880px'
            : !showPollResults &&
              (variant === 'live-event' ||
                variant === 'upcoming-event' ||
                variant === 'past-event')
            ? '400px'
            : undefined
        }
        open={open}
        onClose={onClose}
      >
        <>
          {!showPollResults &&
          !pollAlreadyClosed &&
          (variant === 'live-event' || variant === 'upcoming-event') ? (
            <>
              <Styled.Title>Poll pushed to attendees</Styled.Title>
              <Box marginBottom={3}>
                <Styled.Subtitle>
                  Checked-in attendees are now seeing and responding to your
                  poll.
                </Styled.Subtitle>
                <Styled.Subtitle>
                  {' '}
                  When you’re ready, close the poll and we’ll provide you with
                  the results.
                </Styled.Subtitle>
              </Box>
              <Button fullWidth variant="outlined" onClick={handleClosePoll}>
                Close Poll
              </Button>
            </>
          ) : (
            <>
              {loading && (
                <Box textAlign="center" p={4}>
                  <div>Loading poll data...</div>
                </Box>
              )}
              {!loading && !pollData && isPastEvent && (
                <Box textAlign="center" p={4}>
                  <div>No poll data available</div>
                </Box>
              )}
              {!loading && pollData && (
                <>
                  {isPastEvent && allPolls.length === 1 && (
                    <Styled.SinglePollTitle>Event Poll</Styled.SinglePollTitle>
                  )}
                  <Box textAlign="center">
                    {isPastEvent && allPolls.length > 1 && (
                      <>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Styled.NavLinkAllPolls onClick={onClose}>
                            <ChevronLeftIcon fontSize="small" />
                            All Event Polls
                          </Styled.NavLinkAllPolls>
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          marginBottom={2}
                        >
                          {currentPollIndex > 0 ? (
                            <Styled.NavLinkText onClick={handlePreviousPoll}>
                              <ChevronLeftIcon fontSize="small" />
                              Previous Poll
                            </Styled.NavLinkText>
                          ) : (
                            <Box width="120px" />
                          )}

                          <Styled.PollCounterText>
                            Poll{' '}
                            <span style={{ fontWeight: 'bold' }}>
                              {currentPollIndex + 1}{' '}
                            </span>
                            of {allPolls.length}
                          </Styled.PollCounterText>

                          {currentPollIndex < allPolls.length - 1 ? (
                            <Styled.NavLinkText onClick={handleNextPoll}>
                              Next Poll
                              <ChevronRightIcon fontSize="small" />
                            </Styled.NavLinkText>
                          ) : (
                            <Box width="100px" />
                          )}
                        </Box>
                      </>
                    )}
                    <Styled.ResultsContainer
                      sx={{
                        boxShadow:
                          variant === 'past-event'
                            ? '0px 6px 20px rgba(0, 0, 0, 0.1)'
                            : 'none',
                      }}
                    >
                      {isPastEvent && (
                        <Styled.SinglePollContainer>
                          <img
                            src={Poll}
                            alt="Poll icon"
                            style={{ marginRight: '24px' }}
                          />
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                          >
                            <Styled.PollQuestion>
                              “{pollData.question}”
                            </Styled.PollQuestion>
                          </Box>
                        </Styled.SinglePollContainer>
                      )}

                      {!isPastEvent && (
                        <>
                          <Styled.Title2>Poll closed</Styled.Title2>
                        </>
                      )}
                      <Styled.Subtitle2>Results</Styled.Subtitle2>
                      {loading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                          Loading poll results...
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            border: '1px solid #E6E6E6',

                            padding: '24px 16px',
                            borderRadius: '8px',
                          }}
                        >
                          <Styled.Attendees>
                            {pollData.totalRespondents} of{' '}
                            {pollData.totalAttendees} check-in attendees
                            responded to poll
                          </Styled.Attendees>

                          <Stack spacing={1} mt={3}>
                            {pollData.answers.map((answer, index) => {
                              const percent =
                                (answer.count / pollData.totalRespondents) * 100

                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      height: '100%',
                                      width: animate ? `${percent}%` : 0,
                                      bgcolor: answer.bgColor,
                                      transition: 'width 1s ease-out',
                                      zIndex: 0,
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      zIndex: 1,
                                      p: 2,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Styled.AnswerAndCountLabel>
                                      {answer.label}
                                    </Styled.AnswerAndCountLabel>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                    >
                                      <PeopleIcon fontSize="small" />
                                      <Styled.AnswerAndCountLabel>
                                        {answer.count}
                                      </Styled.AnswerAndCountLabel>
                                    </Stack>
                                  </Box>
                                </Box>
                              )
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Styled.ResultsContainer>

                    {variant === 'live-event' &&
                      organizerOrAdmin &&
                      allPolls?.length >= 1 && (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            if (onPushNextPoll) {
                              onPushNextPoll()
                            }
                          }}
                        >
                          Push Next Poll
                        </Button>
                      )}
                  </Box>
                </>
              )}
            </>
          )}
        </>
      </BaseModal>
    </Box>
  )
}
export default PollResultsModal
